// Curriculum en formato CV (fuente del PDF descargable). Mantener alineado con el CV mas reciente.
export default {
  name: 'Nuño Valencia Castrillo',
  headline: 'AI / Cloud Solution Architect',
  tagline: 'I design and build the systems: GenAI, RAG, Cloud and Kubernetes.',
  contact: [
    'curriculum@nunovalencia.info',
    '+34 615 518 638',
    'Madrid, Spain',
    'curriculum.nunovalencia.com',
    'drouiz.com'
  ],
  summary: [
    'I am an architect who builds, not a researcher: my job is architecture plus hands-on delivery of production systems.',
    'Senior AI, Cloud and Platform Architect with extensive experience designing scalable cloud-native solutions across banking, insurance, IoT, industrial, media and eCommerce environments. Strong background in GenAI, LLM-oriented architectures, NLP, RAG, embeddings, vector search, Python/API integration, Kubernetes/OpenShift, AWS, Azure, Infrastructure as Code, MLOps, CI/CD, security and observability. Experienced translating business requirements into technical architectures and leading cross-functional delivery teams.'
  ],
  skills: [
    ['AI / GenAI', 'GenAI, LLMs, Agentic AI, RAG, Embeddings, Vector Databases, Vector Search, NLP, Semantic Search, AI APIs, MLOps'],
    ['Cloud & Platform', 'AWS, Azure, Google Cloud, DigitalOcean, OpenShift, OpenStack, Kubernetes, Docker, Hybrid Cloud'],
    ['Infrastructure as Code', 'Terraform, Ansible, AWS CDK, Vagrant, Helm'],
    ['DevOps & CI/CD', 'GitLab CI/CD, ArgoCD, Jenkins, CI/CD, DevOps'],
    ['Observability', 'Prometheus, Grafana, Loki, Tempo, CloudWatch, Azure Monitor, ELK'],
    ['Programming', 'Python, JavaScript, Node.js, Go, C/C++, Flask'],
    ['Data & Analytics', 'PySpark, Pandas, Azure Data Factory, Databricks, Step Functions, ML/AI Models'],
    ['Databases & Search', 'SQL Server, MySQL, Oracle, PostgreSQL, MongoDB, Neo4j, Elasticsearch'],
    ['Messaging & Integration', 'Kafka, RabbitMQ, REST APIs, Microservices, Enterprise Integration'],
    ['IoT & Edge', 'IoT, Edge Computing, AWS IoT Core, Modbus, Arduino, Raspberry Pi, PLC'],
    ['ERP & Web', 'Odoo, Vue.js, Angular, React, WordPress, HTML/CSS'],
    ['Leadership', 'Solution Architecture, Technical Leadership, Team Leadership, Stakeholder Management, Jira, Confluence']
  ],
  softSkills: [
    'Architecture plus hands-on delivery of production systems.',
    'Translating business requirements into clear technical architectures; trade-offs documented and defended.',
    'Technical and team leadership: teams of up to 20 people in IoT and Smart Factory projects.',
    'Stakeholder management and cross-functional delivery in banking, insurance, industrial and eCommerce.'
  ],
  experience: [
    {
      company: 'Allianz', place: 'Madrid, Spain', role: 'AI, Infrastructure and Integration Architect', dates: '11/2025 - 09/2026',
      text: 'Banking and insurance sector: cloud-native architectures, infrastructure, integrations and enterprise delivery patterns in regulated financial environments.',
      bullets: [
        'Defined solution architectures, integrations, APIs and cloud/platform patterns.',
        'Designed RAG architectures combining enterprise document retrieval, embeddings, vector databases and LLM-based generation.',
        'Designed integration points between AI services, Python APIs, microservices and enterprise platforms.',
        'Applied Kubernetes/OpenShift, CI/CD, security and observability practices.'
      ],
      tech: 'Python, Microservices, Kubernetes, OpenShift, AWS, Azure, Terraform, Ansible, MLOps, CI/CD'
    },
    {
      company: 'Alisys', place: 'Gijón, Spain', role: 'Team Lead - Robotic Platform', dates: '02/2025 - 05/2025',
      text: 'Led an industrial robotic platform team and European projects, defining architecture and integrating robotics with IoT.',
      bullets: [
        'Built and maintained a cloud-native robotic platform on OpenShift; CI/CD with GitLab and ArgoCD, RBAC, TLS and pod security.',
        'Integrated AI-model capabilities and NLP/LLM interaction patterns for conversational interfaces.'
      ],
      tech: 'OpenStack, OpenShift, Prometheus, Grafana, Loki, Tempo, Ceph, RabbitMQ, Keycloak, ROS, Node.js, Angular'
    },
    {
      company: 'Amazon (Freelance)', place: 'Madrid, Spain', role: 'AWS Architect', dates: '08/2024 - 12/2024',
      text: 'Led architecture and development of an IoT energy-management system for buildings, integrating Edge and AWS serverless components.',
      bullets: [
        'Hybrid Cloud/Edge architecture and IoT data pipelines.',
        'IAM, Security Groups, VPN and IaC with Terraform and AWS CDK.',
        'Led the technical team; CI/CD and AI/ML models for energy efficiency.'
      ],
      tech: 'AWS IoT Core, Lambda, DynamoDB, Timestream, Python, CDK, Terraform, Ansible'
    },
    {
      company: 'Renfe (Freelance)', place: 'Madrid, Spain', role: 'Software and Data Architect', dates: '04/2024 - 07/2024',
      text: 'Graph-based platform for security-incident analysis, linking operational data in Neo4j and deploying microservices on OpenShift/Azure.',
      tech: 'Neo4j, OpenShift, Azure, Databricks, Azure Data Factory, Python, PySpark'
    },
    {
      company: 'Repsol (Freelance)', place: 'Madrid, Spain', role: 'Software Architect', dates: '11/2023 - 03/2024',
      text: 'Scalable analytics architecture for oil & gas production forecasting and infrastructure choke-point detection.',
      tech: 'Azure Data Factory, Azure Functions, SQL, Oracle, Python, ML/AI models'
    },
    {
      company: 'Indra (Freelance)', place: 'Madrid, Spain', role: 'Architect and Team Lead', dates: '01/2023 - 11/2023',
      text: 'Led the redesign of a legacy C++ energy transmission control system into a cloud-native SCADA platform using Python and Go.',
      bullets: [
        'Microservices on Kubernetes/OpenShift across hybrid cloud.',
        'ArgoCD, Terraform, Ansible and full observability (Prometheus, Grafana, Loki, Tempo).',
        'DevOps, MLOps and AIOps practices; IoT/Edge integration.'
      ]
    },
    {
      company: 'Nexiona (Freelance)', place: 'Barcelona, Spain', role: 'CTO / Tech Lead / Solution Architect', dates: '04/2022 - 01/2023',
      text: 'Led a multidisciplinary team of up to 20 people delivering IoT and Smart Factory projects and evolving the company IoT platform.',
      tech: 'Kubernetes, OpenShift, ArgoCD, GitLab CI/CD, Keycloak, Prometheus, Grafana, Azure Data Factory, Power BI'
    },
    {
      company: 'LetMePark (Freelance)', place: 'Madrid, Spain', role: 'Solution Architect', dates: '',
      text: 'Voice-enabled parking reservation platform compatible with Alexa, Google Assistant and Siri, deployed as microservices on OpenShift/AWS.',
      tech: 'OpenShift, AWS, Keycloak, GitLab CI/CD, Vue.js, Node.js, Python'
    },
    {
      company: 'Various clients', place: 'Madrid, Spain', role: 'Freelance & Side Projects', dates: '2019 - 2022',
      text: 'Freelance and entrepreneurial projects alongside or between full-time positions.',
      bullets: [
        'SGAE: VMware-to-OpenShift cloud and microservices modernization.',
        'Crypto trading automation: microservices trading and backtesting platform on OpenShift/AWS.',
        'Odoo: ERP/CRM consulting, custom modules and Kubernetes deployments.',
        'AI e-commerce generator; Kubernetes multi-cloud app installer; IoT/Edge energy optimization; SEO and content automation.'
      ]
    },
    {
      company: 'Santander Bank', place: 'Madrid, Spain', role: 'Microservice Architect', dates: '09/2019 - 01/2020',
      text: 'Progressive migration of a Java monolith to Node.js/Express microservices with Angular, OpenShift/Kubernetes and Elasticsearch.'
    },
    {
      company: 'IBM', place: 'Madrid, Spain', role: 'Microservice and Data Architect', dates: '04/2019 - 11/2019',
      text: 'Cloud-native AML analytics platform combining anomaly detection, graph relationships, ML and NLP for Adverse Media Screening (Neo4j, Python/Keras, Flask, Kafka, Terraform, Ansible).'
    },
    {
      company: 'Telefónica (External)', place: 'Madrid, Spain', role: 'Back End Developer', dates: '08/2018 - 04/2019',
      text: 'Network-path analysis backend using Python/Flask, Node.js, RabbitMQ and Neo4j, with Docker Compose deployment and Angular frontend integration.'
    },
    {
      company: 'Drouiz', place: 'León, Spain', role: 'Developer', dates: '12/2012 - 10/2018',
      text: 'IoT, web development, industrial software integration, infrastructure automation, Odoo and WordPress.'
    },
    {
      company: 'ECAT', place: 'Landivisiau, France', role: 'Electrical Engineer', dates: '05/2012 - 12/2012',
      text: 'Electrical panels, PLC programming and commissioning of automation systems for poultry farms.'
    },
    {
      company: 'Freelance', place: 'León, Spain', role: 'Electrical and Building Services Engineer', dates: '11/2009 - 03/2012',
      text: 'Electrical engineering and building-services projects: factory automation, HVAC, CCTV, alarms, access control and energy efficiency.'
    },
    {
      company: 'TYPSA', place: 'Barcelona, Spain', role: 'Field Engineer', dates: '11/2007 - 03/2009',
      text: 'High-voltage and emergency electrical infrastructure works at Barcelona Airport: inspections, documentation and safety compliance.'
    },
    {
      company: 'Instalaciones Valencia', place: 'León, Spain', role: 'Electrician / Site Supervisor', dates: '1998 - 2007',
      text: 'Industrial, commercial and low-voltage installations, automation projects, site supervision and team leadership of up to 12 technicians.'
    }
  ],
  education: [
    ['University of León - Ingeniero Técnico Industrial (Electricity and Electronics)', '2000 - 2008'],
    ['Universidade de Passo Fundo (Brazil) - Academic exchange', '2007 - 2008'],
    ['Boream - Docker and Kubernetes seminar', '2019'],
    ['Red Hat OpenShift seminar', '2019'],
    ['Businessman Association of León - Business creation and self-employment', '2004 - 2005'],
    ['University of León - PLC programming', '2004']
  ],
  languages: [
    ['Spanish', 'Native'], ['English', 'C1'], ['Portuguese', 'C1'], ['French', 'A1'], ['Japanese', 'A1']
  ],
  awards: [
    '2nd - Startup Weekend León', '1st - Endesa Hackathon (Energy Challenger)', '3rd - App Challenger (NASA)',
    'PSD2 Hackathon (Banks API)', '2nd - Imaguru Blockchain Hackathon', 'Open Future - Telefónica Foundation',
    'Castilla y León Startup Accelerator'
  ]
}
